import { Collapse, UnstyledButton } from "@mantine/core";
import { ChevronDown, Settings2 } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";

import type { Catalog, Job } from "../../../api/generated/model";
import { UploadState } from "../../../platform/media/image-upload";
import { PromptBox } from "../../../platform/studio/composer/PromptBox";
import {
  ModelChoice,
  OutputsChoice,
  RatioChoice,
} from "../../../platform/studio/composer/Settings";
import { ComposerErrors, PriceAndStart } from "../../../platform/studio/composer/Start";
import type { Composer } from "../../../platform/studio/composer/use-composer";
import { actionOf, type ActionTool } from "../../../platform/studio/tools";
import { AdvancedPanel } from "../../../platform/studio/video/AdvancedPanel";
import { MultiShotSwitch } from "../../../platform/studio/video/MultiShotSwitch";
import { NegativePrompt } from "../../../platform/studio/video/NegativePrompt";
import { ShotsEditor } from "../../../platform/studio/video/ShotsEditor";
import { SoundToggle } from "../../../platform/studio/video/SoundToggle";
import { useVideoParts } from "../../../platform/studio/video/use-video-parts";
import { DurationChoice, ResolutionChoice } from "../../../platform/studio/video/VideoSettings";
import classes from "./create.module.css";

const NO_SHAPE = () => undefined;
const STARTED = () => undefined;

/**
 * The prototype's video actions, in its order, each the Studio's action of that name; Generate is
 * none, its frames, elements and shots the composer's own inputs (decision 21).
 */
export const TABS = [
  ["Generate", null],
  ["Edit video", "videoEdit"],
  ["Extend", "videoExtend"],
  ["Upscale", "videoUpscale"],
  ["Reframe", "videoReframe"],
  ["Dub", "videoDubbing"],
  ["Lip-sync", "lipSync"],
  ["Motion control", "motionControl"],
  ["Background", "editBackground"],
  ["Multi-input", "multiInput"],
  ["Reference", "referencedGeneration"],
] as const;

const GENERATE = "Text or frames to video. Add a start and end frame, elements or shots.";

/** An action a model of the catalog takes, and the composer does (the Actions panel's rule). */
function offered(catalog: Catalog, action: ActionTool | null): boolean {
  return action === null || (action.ready && catalog.video_models.some(action.takes));
}

function ActionTabs(props: { c: Composer; onChoose: (action: ActionTool | null) => void }) {
  const { c, onChoose } = props;
  return (
    <>
      <div className={classes.tabs} aria-label="Video actions">
        {TABS.map(([label, id]) => {
          const action = actionOf(id);
          return (
            <button
              key={label}
              type="button"
              className={classes.tab}
              aria-pressed={(c.action?.id ?? null) === id}
              disabled={!offered(c.catalog, action)}
              onClick={() => onChoose(action)}
            >
              {label}
            </button>
          );
        })}
      </div>
      <p className={classes.dim}>{c.action?.description ?? GENERATE}</p>
    </>
  );
}

export interface VideoFormProps {
  catalog: Catalog;
  /**
   * The action it opens on, chosen once through the tabs' own path; after it, the tabs are the
   * person's. Simple's Make it longer and Change something (spec ui-perf/simple-video-compose-tools);
   * Pro's page passes none and opens on Generate.
   */
  initialAction?: "videoExtend" | "videoEdit" | undefined;
  /** A start's own: Simple's page reads its results again. Pro's page passes none. */
  onStarted?: ((job: Job) => void) | undefined;
}

/**
 * The prototype's video composer card on the Studio's own video parts (`useVideoParts`): the same
 * draft, slots, tools, price and start as the Studio's, laid out as the prototype lays them.
 */
export function VideoForm({ catalog, initialAction, onStarted = STARTED }: VideoFormProps) {
  const parts = useVideoParts({
    catalog,
    medium: "video",
    initialSource: null,
    initialType: undefined,
    clone: undefined,
    onStarted,
    onShapeChange: NO_SHAPE,
  });
  const { c } = parts;
  // The page's action, once, before the first paint: a rerender or a tab the person picks after
  // never brings it back.
  const seeded = useRef(false);
  useLayoutEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    if (initialAction !== undefined) parts.chooseAction(actionOf(initialAction));
  }, [initialAction, parts]);
  const [door, setDoor] = useState(false);
  const tabs = <ActionTabs c={c} onChoose={parts.chooseAction} />;
  // A tool with a form of its own (Upscale, Reframe, Dub…) replaces the card under the tabs.
  // In the card, not floating over the page as the Studio's floating panel does (GlassPanel's
  // inline).
  const form = parts.toolForm({ above: tabs, overlay: null, inline: true });
  if (form !== null) return form;
  // Live's "Create Element" covers the card while it is open.
  if (parts.cover !== undefined) return parts.cover;
  return (
    <>
      {tabs}
      {c.from}
      <div className={classes.field}>
        <span className={classes.fieldLabel}>Prompt</span>
        {c.draft.multiShot && c.multiShot !== null ? (
          <ShotsEditor c={c} />
        ) : (
          <>
            <div className={classes.prompt}>
              <PromptBox c={c} tags={parts.promptTags} />
            </div>
            <span className={`${classes.dim} ${classes.count}`}>
              {c.draft.prompt.length} / {c.promptMax}
            </span>
          </>
        )}
      </div>
      <div className={classes.inputs}>
        <span className={classes.lab}>Inputs</span>
        {parts.header}
      </div>
      {/* Kling 4.0's Voice Settings under the inputs (G2-kling-one-voice-list). */}
      {parts.voicesPanel}
      <MultiShotSwitch c={c} />
      <UploadState upload={c.frameUpload} />
      <UploadState upload={c.endUpload} />
      <UploadState upload={c.referenceUpload} />
      <UploadState upload={c.keyframeUpload} />
      <UploadState upload={c.sourceVideoUpload} />
      <UploadState upload={c.videoUpload} />
      <UploadState upload={c.audioUpload} />
      <div className={classes.settings}>
        <span className={classes.pair}>
          <span className={classes.lab}>Shape</span>
          <RatioChoice c={c} />
        </span>
        <span className={classes.pair}>
          <span className={classes.lab}>Length</span>
          <DurationChoice c={c} />
        </span>
        <span className={classes.pair}>
          <span className={classes.lab}>Resolution</span>
          <ResolutionChoice c={c} />
        </span>
        <span className={classes.pair}>
          <span className={classes.lab}>Outputs</span>
          <OutputsChoice c={c} />
          <SoundToggle c={c} />
        </span>
      </div>
      {c.advancedOffered ? (
        <>
          <UnstyledButton
            className={classes.door}
            aria-expanded={door}
            onClick={() => setDoor(!door)}
          >
            <Settings2 size={14} aria-hidden />
            <span>
              <b>Advanced:</b> guidance, negative prompt and the model&apos;s other settings
            </span>
            <ChevronDown size={14} className={classes.doorCaret} aria-hidden />
          </UnstyledButton>
          <Collapse expanded={door}>
            <AdvancedPanel c={c} />
          </Collapse>
        </>
      ) : null}
      <NegativePrompt c={c} />
      <div className={classes.gobar}>
        <ModelChoice c={c} />
        <span className={classes.grow} />
        <PriceAndStart c={c} />
      </div>
      <ComposerErrors c={c} />
      {c.dialog}
    </>
  );
}
